import fs from 'fs/promises';
import path from 'path';
import type { Plugin } from 'vite';

import { createTheme } from './createTheme';
import { safeParseThemeJson, type Theme } from './schemas';

interface ThemePluginOptions {
  // Path to theme config file
  themeJsonPath: string;
  // Function to transform theme to CSS vars
  createThemeVars: (theme: Theme) => string;
}

export default function themePlugin(
  _options?: Partial<ThemePluginOptions>,
): Plugin {
  const options: ThemePluginOptions = {
    themeJsonPath: './theme.json',
    createThemeVars: (theme) => createTheme(theme),
    ..._options,
  };

  let cachedCss: string | null = null;
  const virtualModuleId = 'virtual:theme-vars';
  const resolvedVirtualModuleId = '\0' + virtualModuleId;

  return {
    name: 'vite-plugin-theme',

    async buildStart() {
      // Read and transform theme at startup
      try {
        const themeContent = await fs.readFile(options.themeJsonPath, 'utf-8');
        const themeConfig: Theme | null = safeParseThemeJson(themeContent);
        cachedCss = themeConfig ? options.createThemeVars(themeConfig) : null;
      } catch (e) {
        this.error(`Failed to read theme file: ${e.message}`);
      }
    },

    configureServer(server) {
      const resolvedPath = path.resolve(options.themeJsonPath);
      // Watch theme file for changes in dev mode
      server.watcher.add(resolvedPath);

      server.watcher.on('change', async (changedPath) => {
        if (changedPath === resolvedPath) {
          try {
            const componentsJsonFile = await fs.readFile(resolvedPath, 'utf-8');
            const themeConfig: Theme | null =
              safeParseThemeJson(componentsJsonFile);
            cachedCss = themeConfig
              ? options.createThemeVars(themeConfig)
              : null;

            // Notify clients to reload CSS
            // TOOD: i don't know if full reload is correct
            server.ws.send({
              type: 'full-reload',
              path: '*',
            });
            server.moduleGraph.invalidateAll();
          } catch (e) {
            server.config.logger.error(`Failed to update theme: ${e.message}`);
          }
        }
      });
    },

    resolveId(id) {
      if (id === virtualModuleId) {
        return resolvedVirtualModuleId;
      }
    },

    load(id) {
      if (id === resolvedVirtualModuleId && cachedCss) {
        return `
          // Remove any existing theme styles first
          document.querySelector('style[data-vite-theme]')?.remove()
          
          const style = document.createElement('style')
          style.setAttribute('data-vite-theme', '')
          style.textContent = ${JSON.stringify(cachedCss)}
          document.head.insertBefore(style, document.head.firstChild)
        `;
      }
    },

    transformIndexHtml(html) {
      if (!cachedCss) {
        return;
      }

      return {
        html,
        tags: [
          {
            tag: 'style',
            attrs: {
              'data-vite-theme': '',
              // Add lower priority by inserting at start of head
              'data-inject-first': '',
            },
            children: cachedCss,
            injectTo: 'head-prepend', // Change to prepend instead of default append
          },
        ],
      };
    },
  };
}
